/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  var FILTER_INPUT_SELECTOR = "[data-dd-manual-filter]";
  var FILTER_CLEAR_SELECTOR = "[data-dd-manual-filter-clear]";
  var PRODUCT_SELECTOR = "product-list product-item[data-dd-filter-handle]";
  var EMPTY_SELECTOR = "[data-dd-manual-filter-empty]";
  var COUNT_SELECTOR = ".product-facet__meta-bar-item--count";

  function toDatasetKey(group) {
    return group.split("-").map(function (part) {
      return part.charAt(0).toUpperCase() + part.slice(1);
    }).join("");
  }

  function getSelectedFilters() {
    return Array.prototype.slice.call(document.querySelectorAll(FILTER_INPUT_SELECTOR)).reduce(function (groups, input) {
      var group = input.dataset.ddManualFilter;

      if (!groups[group]) {
        groups[group] = [];
      }

      if (input.checked) {
        groups[group].push(input.value);
      }

      return groups;
    }, {});
  }

  function productMatchesGroup(product, group, selectedValues) {
    if (!selectedValues.length) {
      return true;
    }

    var datasetKey = "ddFilter" + toDatasetKey(group);
    var productValues = (product.dataset[datasetKey] || "").split("|").filter(Boolean);

    return selectedValues.some(function (value) {
      return productValues.indexOf(value) !== -1;
    });
  }

  function updateManualFilterClearButtons(selectedFilters) {
    document.querySelectorAll(FILTER_CLEAR_SELECTOR).forEach(function (button) {
      var group = button.dataset.ddManualFilterClear;
      button.hidden = !selectedFilters[group] || selectedFilters[group].length === 0;
    });
  }

  function updateVisibleProductCount(visibleCount) {
    var countNode = document.querySelector(COUNT_SELECTOR);

    if (!countNode) {
      return;
    }

    var template = visibleCount === 1 ? countNode.dataset.ddCountOne : countNode.dataset.ddCountOther;

    if (!template) {
      template = "__count__ products";
    }

    countNode.textContent = template.replace("__count__", visibleCount);
  }

  function applyManualCollectionFilters() {
    var controls = Array.prototype.slice.call(document.querySelectorAll(FILTER_INPUT_SELECTOR));

    if (!controls.length) {
      return;
    }

    var selectedFilters = getSelectedFilters();
    var products = Array.prototype.slice.call(document.querySelectorAll(PRODUCT_SELECTOR));
    var visibleCount = 0;

    products.forEach(function (product) {
      var isVisible = Object.keys(selectedFilters).every(function (group) {
        return productMatchesGroup(product, group, selectedFilters[group]);
      });

      product.hidden = !isVisible;
      product.style.display = isVisible ? "" : "none";

      if (isVisible) {
        visibleCount += 1;
      }
    });

    updateManualFilterClearButtons(selectedFilters);
    updateVisibleProductCount(visibleCount);

    var emptyState = document.querySelector(EMPTY_SELECTOR);

    if (emptyState) {
      emptyState.hidden = visibleCount > 0;
    }
  }

  function scheduleManualCollectionFilters() {
    window.requestAnimationFrame(function () {
      applyManualCollectionFilters();
    });
  }

  window.directDriveManualCollectionFiltersApply = applyManualCollectionFilters;

  document.addEventListener("change", function (event) {
    var input = event.target.closest(FILTER_INPUT_SELECTOR);

    if (!input) {
      return;
    }

    event.stopPropagation();
    scheduleManualCollectionFilters();
  }, true);

  document.addEventListener("click", function (event) {
    var clearButton = event.target.closest(FILTER_CLEAR_SELECTOR);

    if (!clearButton) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var group = clearButton.dataset.ddManualFilterClear;

    document.querySelectorAll('[data-dd-manual-filter="' + group + '"]').forEach(function (input) {
      input.checked = false;
    });

    scheduleManualCollectionFilters();
  }, true);

  document.addEventListener("shopify:section:load", scheduleManualCollectionFilters);
  document.addEventListener("facet:criteria-changed", function () {
    window.setTimeout(scheduleManualCollectionFilters, 150);
  });
  window.addEventListener("pageshow", scheduleManualCollectionFilters);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleManualCollectionFilters);
  } else {
    scheduleManualCollectionFilters();
  }

  if ("MutationObserver" in window) {
    var observer = new MutationObserver(function (mutations) {
      var shouldApply = mutations.some(function (mutation) {
        return Array.prototype.slice.call(mutation.addedNodes).some(function (node) {
          return node.nodeType === Node.ELEMENT_NODE && (
            node.matches && (node.matches(PRODUCT_SELECTOR) || node.matches(FILTER_INPUT_SELECTOR)) ||
            node.querySelector && (node.querySelector(PRODUCT_SELECTOR) || node.querySelector(FILTER_INPUT_SELECTOR))
          );
        });
      });

      if (shouldApply) {
        scheduleManualCollectionFilters();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
})();
